import React, { useState } from 'react';

import { CircularProgress, Dialog, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import {
  studentTagAssignmentPublish,
  useStudentTagAssignmentDetail
} from 'sdk';

import { ReactComponent as EditIcon } from 'pages/Teachers/ClassroomInsights/assets/edit-preview-icon.svg';
import { ReactComponent as PublishIcon } from 'pages/Teachers/ClassroomInsights/assets/publish-icon.svg';
import { ReactComponent as InfoIcon } from 'pages/Teachers/ClassroomInsights/assets/tomorrow-focus-icon.svg';
import { notifyError, notifySuccess } from 'utils/notifications';

import { MASTERY_LEVEL_TO_LABEL_MAPPER } from '../../constants';
import styles from './style.module.scss';

const StudentWorkDialog = ({
  currentStudent,
  onClose,
  onNext,
  onPrev,
  onPublishSuccess,
  disablePublish
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [prevId, setPrevId] = useState(null);

  const [isPublishDisabled, setIsPublishDisabled] = useState(disablePublish);
  const studentId = currentStudent?.student_tag_assignment_id;

  const { data, loading } = useStudentTagAssignmentDetail({
    studentTagAssignmentId: studentId
  });

  if (studentId !== prevId) {
    setCurrentPage(0);
    setPrevId(studentId);
  }

  const files = data?.student_works.map((student_work) => student_work.url);
  const totalPages = files?.length;
  const currentPageData = data?.student_works[currentPage];

  const handlePublishing = async () => {
    setIsPublishDisabled(true);
    const { success } = await studentTagAssignmentPublish({
      studentTagAssignmentId: studentId
    });

    if (success) {
      notifySuccess('Student work published successfully!');
      onPublishSuccess();
    } else {
      notifyError('Failed to publish student work. Please try again.');
    }
  };

  return (
    <Dialog
      open={!!studentId}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { overflow: 'visible', borderRadius: '12px' } }}
    >
      {/* --- External Global Navigation --- */}
      <IconButton className={styles.globalPrev} onClick={onPrev}>
        <ChevronLeftIcon style={{ fontSize: 40, color: 'white' }} />
      </IconButton>

      <IconButton className={styles.globalNext} onClick={onNext}>
        <ChevronRightIcon style={{ fontSize: 40, color: 'white' }} />
      </IconButton>

      <div className={styles.dialogContainer}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.studentTitle}>
            {data?.student_name || 'Loading...'}
          </span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {loading ? (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={styles.imageSection}>
              {files?.length >= 1 && (
                <img
                  src={currentPageData?.url}
                  alt={`${data?.student_name} Work`}
                  className={styles.mainImage}
                />
              )}

              {/* Internal Pagination - Pages/Files for current student */}
              {totalPages > 1 && (
                <div className={styles.paginationControls}>
                  <IconButton
                    className={styles.pageArrow}
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <span>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <IconButton
                    className={styles.pageArrow}
                    disabled={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </div>
              )}
            </div>

            <div className={styles.insightsSection}>
              <div className={styles.insightHeader}>
                <div>
                  <InfoIcon />
                  <span className={styles.insightsTitle}>AI Insights</span>
                </div>
                <EditIcon className={styles.editIcon} />
              </div>
              <div className={styles.masteryRow}>
                <span className={styles.masteryStatus}>
                  {data?.overall_mastery
                    ? MASTERY_LEVEL_TO_LABEL_MAPPER[data?.overall_mastery]
                    : 'No mastery level'}
                </span>
                <span className={styles.grade}>{currentPageData?.grade}</span>
              </div>

              {currentPageData?.ai_explanation ? (
                <p className={styles.explanation}>
                  {currentPageData?.ai_explanation}
                </p>
              ) : (
                'No AI Insights found'
              )}
              <button
                className={styles.publishBtn}
                onClick={handlePublishing}
                disabled={isPublishDisabled}
              >
                <PublishIcon />

                <span className={styles.publishBtnText}>PUBLISH</span>
              </button>
              <span className={styles.footerText}>
                Update assignment status in the student app based on the student
                mastery.
              </span>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default StudentWorkDialog;
